import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MicIcon from '@mui/icons-material/Mic';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Tooltip from '@mui/material/Tooltip';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { useNavigate } from 'react-router-dom';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { stuurOpname } from '../../utils/opnameUpload';
import { authFetch } from '../../utils/authHelpers';
import { parseUtcDate } from '../../utils/utcDate';
import PageHeader from '../../ui-components/PageHeader';

const statusColors = {
  new: 'info',
  processing: 'warning',
  review_needed: 'secondary',
  completed: 'success',
};

const statusLabels = {
  new: 'Nieuw',
  processing: 'Verwerken',
  review_needed: 'Review nodig',
  completed: 'Afgerond',
};

function MeetingDashboard() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Vanuit de projectflow geopend? Dan is de klantnaam voorgevuld als zoekterm —
  // meetings matchen op klantnaam in de titel, niet op projectnummer.
  const [searchTerm, setSearchTerm] = useState(
    () => new URLSearchParams(window.location.search).get('klant') || ''
  );
  const [tabValue, setTabValue] = useState(0);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [setupKey, setSetupKey] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);

  // Get access token for API calls
  const getAccessToken = async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error('Token acquisition error:', error);
      return null;
    }
  };

  // Get current user's email
  const userEmail = accounts?.[0]?.username || accounts?.[0]?.idTokenClaims?.preferred_username || '';
  const uploadInputRef = useRef(null);
  const [uploadBezig, setUploadBezig] = useState(false);
  const [uploadVoortgang, setUploadVoortgang] = useState(0);
  const [uploadFase, setUploadFase] = useState(null); // 'uploaden' | 'verwerken'

  // Eigen opname uploaden: zelfde weg als de recorder-app (/Api/recorder/upload,
  // met je persoonlijke recorder-sleutel), dus dezelfde transcriptie, bezig-regel
  // en samenvatting. De opname verschijnt binnen een minuut als "⏳ Transcriptie bezig".
  const uploadOpname = async (bestand) => {
    if (bestand.size > 150 * 1024 * 1024) {
      toast.error('Bestand groter dan 150 MB — gebruik hiervoor de recorder-app (Bestand uploaden…), die stuurt in blokken.');
      return;
    }
    setUploadBezig(true);
    setUploadFase('uploaden');
    setUploadVoortgang(0);
    try {
      const sleutelR = await authFetch(`${BACKEND_URL}/Api/recorder/my-key`);
      const sleutel = (await sleutelR.json().catch(() => ({})))?.api_key;
      if (!sleutelR.ok || !sleutel) throw new Error('Geen recorder-sleutel gevonden — maak er eerst een aan via Recorder Setup.');
      await stuurOpname({
        url: `${BACKEND_URL}/Api/recorder/upload`,
        bestand,
        sleutel,
        velden: {
          meeting_date: new Date(bestand.lastModified || Date.now()).toISOString(),
          host_email: userEmail,
          title: bestand.name.replace(/\.[a-z0-9]+$/i, ''),
        },
        onVoortgang: setUploadVoortgang,
      });
      // De transcriptie start pas ná de upload; de "⏳ Transcriptie bezig"-regel
      // verschijnt daardoor een paar seconden later. Eén verversing miste dat
      // moment, waardoor de opname "ineens weg" leek (Joris, 10 sep 2026).
      setUploadFase('verwerken');
      toast.success('Opname binnen — de transcriptie draait. Hij verschijnt zo in de lijst als "Transcriptie bezig".');
      for (const wacht of [2000, 4000, 6000, 8000, 10000, 15000]) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, wacht));
        // eslint-disable-next-line no-await-in-loop
        const lijst = await fetchMeetings({ showLoading: false });
        // fetchMeetings gaf niets terug, dus liep de lus altijd vol (Codex, 11 sep).
        // Zodra de bezig-regel er staat zijn we klaar.
        if (Array.isArray(lijst) && lijst.some((m) => String(m.title || '').startsWith('⏳'))) break;
      }
    } catch (e) {
      toast.error(`Uploaden mislukt: ${e.message}`);
    } finally {
      setUploadBezig(false);
      setUploadFase(null);
      setUploadVoortgang(0);
    }
  };


  
  // Admins KUNNEN alle meetings zien, maar standaard staat ook voor hen het
  // eigen-meetings-filter aan: alles-zien is een bewuste keuze (schakelaar),
  // geen stille standaard. De engine handhaaft dit sowieso server-side
  // (SEC-004) — niet-admins krijgen daar altijd hun eigen scope.
  const ADMIN_EMAILS = ['joris@fullframe.nl'];
  const isAdmin = ADMIN_EMAILS.includes(userEmail?.toLowerCase());
  const [showAllMeetings, setShowAllMeetings] = useState(false);

  // Fetch meetings from AI Engine (filtered by user email, unless admin-toggle)
  const fetchMeetings = async (options = { showLoading: true }) => {
    if (options.showLoading) {
      setLoading(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams();
      if (userEmail && !(isAdmin && showAllMeetings)) {
        params.append('user_email', userEmail);
      }
      const url = `${AI_ENGINE_URL}/api/v1/meetings/${params.toString() ? '?' + params.toString() : ''}`;
      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      // Meetings met een lopende undo-timer niet terug laten komen via refresh.
      const zichtbaar = data.filter((m) => !pendingDeleteIdsRef.current.has(m.id));
      setMeetings(zichtbaar);
      // Teruggeven, zodat de uploadlus kan stoppen zodra de bezig-regel er staat.
      return zichtbaar;
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError(`Kon meetings niet laden: ${err.message}`);
    } finally {
      if (options.showLoading) {
        setLoading(false);
      }
    }
  };

  // Verwijderen zónder confirm-popup: de rij verdwijnt meteen, de échte DELETE
  // wacht 10 s zodat "Ongedaan maken" hem kan afbreken (Gmail-patroon; verzoek
  // 20 aug). Timers per meeting-id zodat meerdere deletes elkaar niet bijten.
  const deleteTimersRef = useRef({});
  const deleteToastsRef = useRef({});
  // Tombstones blijven de hele sessie staan: een trage GET met een pre-delete
  // snapshot mag een verwijderde meeting niet terugzetten.
  const pendingDeleteIdsRef = useRef(new Set());
  const doDelete = async (meetingId) => {
    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meetingId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
      return true; // tombstone blijft — stale fetches filteren hem eruit
    } catch (err) {
      console.error('Error deleting meeting:', err);
      pendingDeleteIdsRef.current.delete(meetingId);
      return false;
    }
  };
  // Unmount binnen de undo-periode = de delete alsnog DIRECT uitvoeren —
  // anders bleef de meeting stiekem bestaan terwijl de UI 'verwijderd' zei.
  useEffect(() => () => {
    Object.entries(deleteTimersRef.current).forEach(([mid, timer]) => {
      clearTimeout(timer);
      // Toast weg vóór de delete: 'Ongedaan maken' na navigatie zou een stille
      // leugen zijn (de delete loopt dan al).
      if (deleteToastsRef.current[mid]) toast.dismiss(deleteToastsRef.current[mid]);
      doDelete(Number(mid));
    });
    deleteTimersRef.current = {};
    deleteToastsRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteMeeting = (meetingId) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    pendingDeleteIdsRef.current.add(meetingId);
    setMeetings((prev) => prev.filter((m) => m.id !== meetingId));

    const toastId = toast(
      (t) => (
        <span>
          Meeting verwijderd —{' '}
          <button
            type="button"
            onClick={() => {
              clearTimeout(deleteTimersRef.current[meetingId]);
              delete deleteTimersRef.current[meetingId];
              pendingDeleteIdsRef.current.delete(meetingId);
              if (meeting) setMeetings((prev) => (prev.some((m) => m.id === meetingId) ? prev : [meeting, ...prev]));
              toast.dismiss(t.id);
              toast.success('Verwijderen teruggedraaid');
            }}
            style={{ border: 'none', background: 'none', color: '#6366f1', fontWeight: 700, cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}
          >
            Ongedaan maken
          </button>
        </span>
      ),
      { duration: 10000, icon: '🗑️' }
    );
    deleteToastsRef.current[meetingId] = toastId;

    deleteTimersRef.current[meetingId] = setTimeout(async () => {
      delete deleteTimersRef.current[meetingId];
      toast.dismiss(toastId);
      const ok = await doDelete(meetingId);
      if (!ok) {
        toast.error('Kon meeting niet verwijderen');
        fetchMeetings(); // rij terug — verwijderen is niet gelukt
      }
    }, 10000);
  };

  // Load meetings on page load (meetings komen uit de FF Recorder)
  useEffect(() => {
    fetchMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllMeetings]);

  // Dubbele opnames van dezelfde meeting (twee collega's namen allebei op):
  // zelfde titel + zelfde dag + start binnen 15 min → één hoofdrij (langste
  // opname) met de rest als sub-vermelding eronder (verzoek 20 aug, Flevoland).
  const duplicaatVan = React.useMemo(() => {
    const map = new Map(); // secundair-id → primaire meeting
    const key = (m) => `${String(m.title || '').trim().toLowerCase()}|${String(m.meeting_date || '').slice(0, 10)}`;
    const groepen = new Map();
    meetings.forEach((m) => {
      const k = key(m);
      if (!groepen.has(k)) groepen.set(k, []);
      groepen.get(k).push(m);
    });
    groepen.forEach((groep) => {
      if (groep.length < 2) return;
      const tijd = (m) => new Date(m.meeting_date || 0).getTime();
      const gesorteerd = [...groep].sort((a, b) => tijd(a) - tijd(b));
      // Cluster op startmoment ≤ 15 min uit elkaar.
      let cluster = [gesorteerd[0]];
      const clusters = [cluster];
      for (let i = 1; i < gesorteerd.length; i += 1) {
        // t.o.v. de clusterSTART — transitief aan elkaar plakken maakte 10:00
        // en 10:28 anders één cluster (Codex r10).
        if (tijd(gesorteerd[i]) - tijd(cluster[0]) <= 15 * 60 * 1000) cluster.push(gesorteerd[i]);
        else { cluster = [gesorteerd[i]]; clusters.push(cluster); }
      }
      clusters.forEach((c) => {
        if (c.length < 2) return;
        const primair = [...c].sort((a, b) => (b.duration || 0) - (a.duration || 0))[0];
        c.forEach((m) => { if (m.id !== primair.id) map.set(m.id, primair); });
      });
    });
    return map;
  }, [meetings]);

  const subOpnames = React.useMemo(() => {
    const map = new Map(); // primair-id → [sub-meetings]
    duplicaatVan.forEach((primair, subId) => {
      const sub = meetings.find((m) => m.id === subId);
      if (!sub) return;
      if (!map.has(primair.id)) map.set(primair.id, []);
      map.get(primair.id).push(sub);
    });
    return map;
  }, [duplicaatVan, meetings]);

  // Twee opnames van dezelfde meeting samenvoegen: transcripten achter elkaar,
  // deelnemers gecombineerd, samenvatting opnieuw — de tweede opname verdwijnt.
  const [mergeBusy, setMergeBusy] = useState(null);
  const handleMergeMeetings = async (primair, sub) => {
    if (!(await bevestig({ titel: 'Opnames samenvoegen?', tekst: `"${sub.title}" (${sub.duration || '?'} min) wordt toegevoegd aan "${primair.title}" en daarna gearchiveerd. De samenvatting wordt opnieuw gemaakt.`, knop: 'Samenvoegen' }))) return;
    setMergeBusy(sub.id);
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${primair.id}/merge/${sub.id}`, { method: 'POST' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      toast.success('Opnames samengevoegd — samenvatting is opnieuw gemaakt');
      fetchMeetings();
    } catch (e) { toast.error(`Samenvoegen mislukt: ${e.message}`); }
    finally { setMergeBusy(null); }
  };

  // Filter meetings based on search and tab
  const filteredMeetings = meetings.filter((meeting) => {
    if (duplicaatVan.has(meeting.id)) return false; // sub-opname: toon onder de hoofdrij
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      meeting.title?.toLowerCase().includes(searchLower) ||
      meeting.summary?.toLowerCase().includes(searchLower) ||
      meeting.participants?.toLowerCase().includes(searchLower) ||
      meeting.fireflies_meeting_id?.toLowerCase().includes(searchLower);

    if (tabValue === 0) return matchesSearch; // Alle
    if (tabValue === 1) return matchesSearch && meeting.status === 'new';
    if (tabValue === 2) return matchesSearch && meeting.status === 'review_needed';
    if (tabValue === 3) return matchesSearch && meeting.status === 'completed';
    return matchesSearch;
  });

  // Meeting opent als volwaardige pagina (was: popup) — meer ruimte + deelbare URL.
  const handleOpenMeeting = (meeting) => {
    navigate(`/MeetingDashboard/${meeting.id}`);
  };

  const handleDetailUpdate = () => {
    fetchMeetings();
  };

  const handleArchiveMeeting = async (meeting) => {
    if (!(await bevestig({ titel: 'Meeting verbergen?', tekst: 'De meeting verdwijnt uit je overzicht.', knop: 'Verbergen' }))) return;

    try {
      const response = await authFetch(`${AI_ENGINE_URL}/api/v1/meetings/${meeting.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: true }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));
      toast.success('Meeting verborgen');
    } catch (err) {
      console.error('Error archiving meeting:', err);
      toast.error('Kon meeting niet verbergen');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = parseUtcDate(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = parseUtcDate(dateString);
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="groups"
        title="Meetings"
        subtitle="Opgenomen meetings bekijken en verwerken"
        rightActions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {isAdmin && (
              <FormControlLabel
                control={<Switch size="small" checked={showAllMeetings} onChange={(e) => setShowAllMeetings(e.target.checked)} />}
                label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Alle meetings (admin)</Typography>}
                sx={{ mr: 0 }}
              />
            )}
            <Tooltip title="Installer + API key gekoppeld aan jouw inlog">
              <Button
                variant="contained"
                size="small"
                startIcon={<MicIcon />}
                onClick={async () => {
                  setSetupOpen(true);
                  setSetupLoading(true);
                  try {
                    const resp = await authFetch(`${BACKEND_URL}/Api/recorder/my-key`);
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    const data = await resp.json();
                    setSetupKey(data);
                  } catch (err) {
                    console.error('[Recorder Setup] failed:', err);
                    toast.error('Kon key niet ophalen: ' + err.message);
                  } finally {
                    setSetupLoading(false);
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Recorder Setup
              </Button>
            </Tooltip>
            {/* Eigen opname uploaden (dictafoon, m4a van je telefoon) — wiki-feedback
                Meeting notes. Gaat via de backend met je recorder-sleutel; grote
                bestanden (> 150 MB) kunnen beter via de recorder-app, die uploadt in blokken. */}
            <input ref={uploadInputRef} type="file" accept=".mp3,.m4a,.wav,audio/*" hidden
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadOpname(f); }} />
            <Tooltip title="Een eigen opname (mp3, m4a, wav) laten transcriberen — bijvoorbeeld van de dictafoon op je telefoon">
              <span>
                <Button variant="outlined" size="small" startIcon={uploadBezig ? <CircularProgress size={14} /> : <UploadFileIcon />}
                  disabled={uploadBezig} onClick={() => uploadInputRef.current?.click()} sx={{ textTransform: 'none' }}>
                  {uploadFase === 'verwerken' ? 'Transcriptie gestart…'
                    : uploadBezig ? `Uploaden… ${uploadVoortgang}%` : 'Audio uploaden'}
                </Button>
              </span>
            </Tooltip>
            {/* Download: primair jouw eigen platform, met menu voor het andere
                (bv. downloaden voor je Mac vanaf een Windows-machine). */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
              sx={{ textTransform: 'none' }}
            >
              Download ▾
            </Button>
            <Menu anchorEl={downloadMenuAnchor} open={Boolean(downloadMenuAnchor)} onClose={() => setDownloadMenuAnchor(null)}>
              {[['win', '🪟 Windows'], ['mac', '🍎 Mac (gesigneerd)']].map(([platform, label]) => (
                <MenuItem key={platform} onClick={() => {
                  window.open(`${BACKEND_URL}/Api/recorder/download?platform=${platform}`, '_blank');
                  setDownloadMenuAnchor(null);
                }}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
            <IconButton onClick={fetchMeetings} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        )}
      />
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        {/* Search bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Zoek op onderwerp, deelnemers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Tabs for filtering */}
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label={`Alle (${meetings.length})`} />
          <Tab label={`Nieuw (${meetings.filter((m) => m.status === 'new').length})`} />
          <Tab label={`Review (${meetings.filter((m) => m.status === 'review_needed').length})`} />
          <Tab label={`Afgerond (${meetings.filter((m) => m.status === 'completed').length})`} />
        </Tabs>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          /* Meetings table */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Datum</TableCell>
                  <TableCell>Meeting</TableCell>
                  <TableCell>Deelnemers</TableCell>
                  <TableCell>Duur</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Acties</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMeetings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        Geen meetings gevonden. Meetings verschijnen hier automatisch na opname met de FF Recorder.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMeetings.map((meeting) => (
                    <React.Fragment key={meeting.id}>
                    <TableRow 
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenMeeting(meeting)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(meeting.meeting_date)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(meeting.meeting_date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {meeting.source === 'local_recording' && (
                            <Chip
                              label="Lokaal"
                              size="small"
                              variant="outlined"
                              color="primary"
                              icon={<MicIcon fontSize="small" />}
                              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.75rem' } }}
                            />
                          )}
                          <Typography fontWeight={500} noWrap title={meeting.title || meeting.summary}>
                            {meeting.title || 'Untitled Meeting'}
                          </Typography>
                        </Box>
                        {meeting.summary && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {meeting.summary?.substring(0, 60)}...
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={meeting.participants || ''}>
                          {meeting.participants || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {meeting.duration ? `${meeting.duration} min` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[meeting.status] || meeting.status}
                          color={statusColors[meeting.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenMeeting(meeting);
                          }}
                          title="Bekijk & verwerk"
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          title="Verberg meeting"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveMeeting(meeting);
                          }}
                        >
                          <VisibilityOffIcon />
                        </IconButton>
                        <IconButton
                          title="Verwijder meeting permanent"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMeeting(meeting.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {meeting.status === 'review_needed' && (
                          <IconButton 
                            title="Markeer als afgerond" 
                            color="success"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {(subOpnames.get(meeting.id) || []).map((sub) => (
                      <TableRow key={`sub-${sub.id}`} hover
                        sx={{ '& td': { py: 0.5, borderBottom: '1px dashed #e2e8f0' }, bgcolor: '#f8fafc' }}>
                        <TableCell colSpan={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 2 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', cursor: 'pointer', flex: 1 }}
                              onClick={() => handleOpenMeeting(sub)}>
                              🎙️ Tweede opname van dezelfde meeting ({formatTime(sub.meeting_date)} · {sub.duration || '?'} min
                              {sub.participants ? ` · ${String(sub.participants).split(',')[0].trim()}` : ''}) — klik om te openen
                            </Typography>
                            <Button size="small" variant="text" disabled={mergeBusy === sub.id}
                              onClick={(e) => { e.stopPropagation(); handleMergeMeetings(meeting, sub); }}
                              sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>
                              {mergeBusy === sub.id ? <CircularProgress size={13} /> : '⤵ Samenvoegen'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Recorder Setup Dialog — generates/returns user's personal recorder key,
          gekoppeld aan MSAL login email zodat uploads nooit op een ander account landen */}
      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>🎙️ Recorder Setup</DialogTitle>
        <DialogContent>
          {setupLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : setupKey ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Deze key is gekoppeld aan <strong>{setupKey.email}</strong>. Al je opnames komen binnen onder dit adres — je ziet ze terug in je eigen overzicht.
              </Alert>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                JE API KEY
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 2 }}>
                <TextField
                  fullWidth
                  value={setupKey.api_key}
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(setupKey.api_key);
                    toast.success('Key gekopieerd');
                  }}
                >
                  Kopieer
                </Button>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Installatie:</Typography>
              <ol style={{ paddingLeft: 20, margin: 0, fontSize: 14, lineHeight: 1.8 }}>
                <li>Download de recorder (knop "Download" hiernaast)</li>
                <li>Open de recorder → rechtermuisklik op het tray-icoon → <strong>Configuratie</strong></li>
                <li>Plak je key in <em>Geavanceerd → Recorder API Key</em></li>
                <li>Vul <strong>jouw eigen</strong> e-mailadres in: <code>{setupKey.email}</code></li>
                <li>Opslaan. Klaar.</li>
              </ol>
              <Alert severity="warning" sx={{ mt: 2 }}>
                ⚠️ Gebruik nooit een collega's e-mail — anders zie je jouw eigen opnames niet terug.
              </Alert>
            </Box>
          ) : (
            <Alert severity="error">Kon key niet ophalen.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupOpen(false)}>Sluiten</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MeetingDashboard;
